import React, { useCallback, useEffect, useState } from "react";

// region Language
import useTranslation from "@languages/useTranslation";
import { ChangePasswordMessages, LoginMessages, SettingMessages } from "@languages/interfaces";
// endregion Language
// region Molecules
import OptionStep from "@molecules/OptionStep";
// endregion Molecules
// region Libraries
import { useAuth } from "@hooks/useAuth";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { IconButton, TextField } from "@mui/material";
import { Visibility, VisibilityOff } from "@libraries/icons";
import { Button, useToast } from "@npm-telluria-tecnologia/telluria-ui";
import Yup from "@libraries/yup";
// endregion Libraries
// region Store
import { api } from "@store/services/rest";
import { ECommandResult } from "@store/enums/commandResult.enum";
// endregion Store
// region Styles
import * as Styled from "./styles";
// endregion Styles

interface FormChangePasswordProps {
  onSubmit: () => void;
}

interface IChangePasswordFormData {
  password: string;
  newPassword: string;
  newPasswordConfirmation: string;
}

const FormChangePassword: React.FC<FormChangePasswordProps> = ({
  onSubmit
}: FormChangePasswordProps) => {

  const { t } = useTranslation();
  const { addToast } = useToast();
  const {
    user,
    checkPassword,
    getDecodedToken
  } = useAuth();
  const token = getDecodedToken();
  const queryParams = `?userId=${token.primarysid}`;

  // region States
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showNewPasswordConfirmation, setShowNewPasswordConfirmation] = useState(false);
  const [filledPassword, setFilledPassword] = useState(false);
  const [filledNewPassword, setFilledNewPassword] = useState(false);
  const [filledNewPasswordConfirmation, setFilledNewPasswordConfirmation] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [prerequisitesMet, setPrerequisitesMet] = useState(false);
  // endregion States

  /** Define the validation types */
  const changePasswordSchema = Yup.object().shape({
    password: Yup.string()
      .trim()
      .required(t(ChangePasswordMessages.currentPasswordRequired)),
    newPassword: Yup.string()
      .trim()
      .required(t(ChangePasswordMessages.newPasswordRequired))
      .min(6, t(ChangePasswordMessages.newPasswordMinimumLength)),
    newPasswordConfirmation: Yup.string()
      .trim()
      .required(t(ChangePasswordMessages.confirmPasswordRequired))
      .oneOf([Yup.ref("newPassword")], t(ChangePasswordMessages.confirmPasswordNotMatch))
  });

  // Form control
  const {
    control,
    handleSubmit,
    formState: { errors: formErrors },
    getValues
  } = useForm<IChangePasswordFormData>({
    resolver: yupResolver(changePasswordSchema),
    reValidateMode: "onChange",
    mode: "onBlur"
  });

  // region Handlers
  const handleClickShowPassword = () => setShowPassword((state) => !state);
  const handleClickShowNewPassword = () => setShowNewPassword((state) => !state);
  const handleClickShowNewPasswordConfirmation = () => setShowNewPasswordConfirmation((state) => !state);

  const handleChangePassword = () => {
    setFilledPassword(
      getValues("password") !== undefined && getValues("password") !== ""
    );
    setFilledNewPassword(
      getValues("newPassword") !== undefined && getValues("newPassword") !== ""
    );
    setFilledNewPasswordConfirmation(
      getValues("newPasswordConfirmation") !== undefined && getValues("newPasswordConfirmation") !== ""
    );
  };

  const handleSubmitForm: SubmitHandler<IChangePasswordFormData> = (
    formData: IChangePasswordFormData
  ) => changePassword(formData);
  // endregion Handlers

  const changePassword = useCallback(async (formData) => {

    setIsLoading(true);

    try {
      // Check if the current password is correct
      const correctPassword = await checkPassword({
        email: user.email,
        password: formData.password
      });

      if (!correctPassword) {
        addToast({
          type: "info",
          title: t(SettingMessages.settingLoginVerificationFailed)
        });

        return;
      }

      // Change password
      const response = await api.post(`/auth/changepassword${queryParams}`, {
        password: formData.newPassword
      });

      const responseData = response.data;

      if (responseData.status === ECommandResult.SUCCESS) {
        addToast({
          type: "success",
          title: t(ChangePasswordMessages.changePasswordSuccess)
        });
        onSubmit();
      }

    } catch (error) {
      addToast({
        type: "error",
        title: t(ChangePasswordMessages.changePasswordFailed)
      });
    } finally {
      setIsLoading(false);
    }
  }, [addToast, checkPassword, getValues, onSubmit, queryParams, t, user.email]);

  useEffect(() => {
    setPrerequisitesMet(!isLoading && filledPassword && filledNewPassword && filledNewPasswordConfirmation);
  }, [isLoading, filledPassword, filledNewPassword, filledNewPasswordConfirmation]);

  return (
    <Styled.FormContainer>
      <form
        onSubmit={handleSubmit(handleSubmitForm)}
        onChange={handleChangePassword}
      >
        <Styled.OptionContainer>
          <OptionStep
            number="1"
            title={t(ChangePasswordMessages.currentPasswordInstruction)}
          >
            <Styled.PasswordContainer>
              <Controller // Current password
                name="password"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    autoComplete="off"
                    label={t(ChangePasswordMessages.currentPasswordLabel)}
                    type={showPassword ? "text" : "password"}
                    variant="outlined"
                    fullWidth
                    size="small"
                    error={!!formErrors.password}
                    helperText={formErrors.password && formErrors.password.message}
                    className="default-field"
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          aria-label="toggle password visibility"
                          edge="end"
                          onClick={handleClickShowPassword}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      )
                    }}
                  />
                )}
              />
            </Styled.PasswordContainer>
          </OptionStep>
          <OptionStep
            number="2"
            title={t(ChangePasswordMessages.newPasswordInstruction)}
          >
            <Styled.PasswordContainer>
              <Controller // New password
                name="newPassword"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    autoComplete="off"
                    label={t(ChangePasswordMessages.newPasswordLabel)}
                    type={showNewPassword ? "text" : "password"}
                    variant="outlined"
                    fullWidth
                    size="small"
                    error={!!formErrors.newPassword}
                    helperText={formErrors.newPassword && formErrors.newPassword.message}
                    className="default-field"
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          aria-label="toggle password visibility"
                          edge="end"
                          onClick={handleClickShowNewPassword}
                        >
                          {showNewPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      )
                    }}
                  />
                )}
              />
            </Styled.PasswordContainer>
          </OptionStep>
          <OptionStep
            number="3"
            title={t(ChangePasswordMessages.confirmPasswordInstruction)}
          >
            <Styled.PasswordContainer>
              <Controller // New password confirmation
                name="newPasswordConfirmation"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    autoComplete="off"
                    label={t(ChangePasswordMessages.newPasswordLabel)}
                    type={showNewPasswordConfirmation ? "text" : "password"}
                    variant="outlined"
                    fullWidth
                    size="small"
                    error={!!formErrors.newPasswordConfirmation}
                    helperText={formErrors.newPasswordConfirmation && formErrors.newPasswordConfirmation.message}
                    className="default-field"
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          aria-label="toggle password visibility"
                          edge="end"
                          onClick={handleClickShowNewPasswordConfirmation}
                        >
                          {showNewPasswordConfirmation ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      )
                    }}
                  />
                )}
              />
              <Styled.ButtonContainer>
                <Button
                  variant="contained"
                  size="small"
                  type="submit"
                  text={t(ChangePasswordMessages.changePasswordButtonTitle)}
                  loading={isLoading}
                  disabled={!prerequisitesMet}
                />
              </Styled.ButtonContainer>
            </Styled.PasswordContainer>
          </OptionStep>
        </Styled.OptionContainer>
      </form>
    </Styled.FormContainer>
  );
};

export default FormChangePassword;